import config from 'react-global-configuration';

import {
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_REQUEST,
  UI_OPERATION_STATUS_SUCCESS,
  setOperationStatus,
} from '@uncinc/react-drupal-core';

import { DAYS_IN_WEEK, DOMAIN_ZELFHULP_GOKKEN, PROGRAM_DURATION_DAYS } from '../../../config/constants';
import { getDaysDifference } from '../../../helpers/formatDate';
import getCurrentDomain from '../../../helpers/getCurrentDomain';
import { roundToQuarter, roundToHalf } from '../../../helpers/rounding';
import { viewGet } from '../Entity/View';
import { fetchMessages } from '../Messages';
import { webformSubmit } from '../Webform';

// The following code is old and could possibly conflict with the new way
// of fetching registration data using endpoints. We're currently only using
// these functions for the nurse practitioner views. Changing this to the
// new way turned out to take more time, since it also requires backend
// changes, so we've left this for now. However, it should be removed in
// a future sprint.
//
// Ticket: https://uncinc.monday.com/boards/3375006290/pulses/8104344749
const dateFormat = (date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export function aggregateStreaks(registrations) {
  const total = Object.keys(registrations).filter((date) => (
    new Date(date).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0)
  ));

  const completed = Object.keys(registrations).filter((date) => {
    const registration = registrations[date];

    return registration.totalAmount !== null;
  });

  let success = 0;
  let failed = 0;

  for (let i = 0; i < completed.length; i++) {
    const date = completed[i];
    const registration = registrations[date];

    if (registration.goal === null) {
      continue;
    }

    if (registration.totalAmount <= registration.goal) {
      success += 1;
    } else {
      failed += 1;
    }
  }

  return {
    total: total.length,
    completed: completed.length,
    success,
    failed,
  };
}

export function aggregateAndGroupRegistrations(entities, initialRegistrations = {}) {
  const submittedData = initialRegistrations;

  entities.forEach((item) => {
    const { data } = item;
    const uuid = item.uuid[0].value;

    if (submittedData[data.registration_date]) {
      // Additional submission.
      submittedData[data.registration_date].items = {
        ...submittedData[data.registration_date].items,
        [uuid]: data,
      };
    } else {
      // New submission.
      submittedData[data.registration_date] = {
        items: {
          [uuid]: data,
        },
        goal: null,
      };
    }
  });

  Object.keys(submittedData).forEach((date) => {
    const registration = submittedData[date];

    const registrationItemKeys = Object.keys(registration.items);

    if (registrationItemKeys.length === 0) {
      submittedData[date].totalAmount = null;
      return;
    }

    const totalAmount = registrationItemKeys.reduce((total, uuid) => {
      const item = registration.items[uuid];

      return total + parseFloat(item.amount);
    }, 0);

    submittedData[date].totalAmount = totalAmount;
  });

  return submittedData;
}

export function filterAwayGoalExercises(entities) {
  return entities.filter((item) => {
    const webformId = Array.isArray(item.webform_id) && item.webform_id[0].target_id;
    if (!webformId) {
      return false;
    }
    return webformId !== 'daily_registration';
  });
}

export function aggregateAndGroupGoals(entities, startDate, registrations) {
  const daysData = { ...registrations };
  const programWeeks = PROGRAM_DURATION_DAYS / DAYS_IN_WEEK;
  const amountOfWeeks = Math.max(programWeeks, Math.ceil(Object.entries(registrations).length / DAYS_IN_WEEK));
  const domain = getCurrentDomain();

  const roundingFunction = domain === DOMAIN_ZELFHULP_GOKKEN ? roundToQuarter : roundToHalf;

  function getGoal(weekNumber, currentValue, endGoal, isImmediate) {
    const diff = (currentValue - endGoal) / programWeeks;

    // The startGoal, the user's goal in the first week, is equal
    // to the currentValue - 1 diff, because your goals in the
    // first week need to be lower than your current usage.
    const startGoal = currentValue - diff;

    // Goals in the last week are equal to what the user
    // has set as their goal usage per day. When they have
    // chosen to stop, this will always be 0, otherwise it
    // will be whatever they chose as their goal.
    // Here, weekNumber is 0-indexed.
    if (weekNumber >= programWeeks - 1 && !Number.isNaN(endGoal)) {
      return roundingFunction(endGoal);
    }

    // In between the first and last week, the goal will be
    // set to the end goal when the user has chosen to immediately
    // work towards their goal. Otherwise it will be gradually.
    if (isImmediate) {
      return roundingFunction(endGoal);
    }

    return roundingFunction(parseFloat(startGoal - (diff * weekNumber)));
  }

  entities.forEach((item) => {
    // We only care about the goal submissions. If there are multiple
    // submissions, we just overwrite the old version.
    if (item.webform_id[0].target_id === 'goal') {
      let currentDate = new Date(startDate);

      const currentUsage = [
        item.data.monday,
        item.data.tuesday,
        item.data.wednesday,
        item.data.thursday,
        item.data.friday,
        item.data.saturday,
        item.data.sunday,
      ];

      const targetedUsage = [
        item.data.goal_monday,
        item.data.goal_tuesday,
        item.data.goal_wednesday,
        item.data.goal_thursday,
        item.data.goal_friday,
        item.data.goal_saturday,
        item.data.goal_sunday,
      ];

      // With the getDay method, sundays are returned as 0.
      // We want to shift this such that mondays are 0 and sundays are 6.
      //
      // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getDay
      let startDateDayInWeek = currentDate.getDay() - 1;

      if (startDateDayInWeek === -1) {
        startDateDayInWeek = 6;
      }

      if (startDateDayInWeek !== 0) {
        // When the start date is not a monday, we need to shift
        // the arrays to the left, so that the first day of the week
        // is the start date.
        for (let i = 0; i < startDateDayInWeek; i++) {
          currentUsage.push(currentUsage.shift());
          targetedUsage.push(targetedUsage.shift());
        }
      }

      const isImmediate = item.data.immediate_or_gradual === 'immediate';

      // Interpolate goals over time.
      for (let i = 0; i < amountOfWeeks; i++) {
        for (let j = 0; j < DAYS_IN_WEEK; j++) {
          const currentValue = parseFloat(currentUsage[j]) || 0;
          const endGoal = parseFloat(targetedUsage[j]) || 0;

          if (daysData[dateFormat(currentDate)]) {
            daysData[dateFormat(currentDate)].goal = getGoal(i, currentValue, endGoal, isImmediate);
          }

          // Go to next day.
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
    }
  });

  return daysData;
}

export function initializeRegistrationDates(registrationsStartDate, programStartDate) {
  const daysData = {};

  // We allow users to change their registration start date, to the past
  // So they can retroactively register days, before they started the program.
  const registrationDaysBeforeProgramStart = getDaysDifference(registrationsStartDate, programStartDate);

  let currentDate = new Date(registrationsStartDate);
  for (let i = 0; i < PROGRAM_DURATION_DAYS + registrationDaysBeforeProgramStart; i++) {
    daysData[dateFormat(currentDate)] = {
      totalAmount: null,
      goal: null,
      items: [],
    };

    // Go to next day.
    currentDate.setDate(currentDate.getDate() + 1);
  }

  const isSameDayOrBefore = (dateToCompare, referenceDate) => {
    if (dateToCompare < referenceDate) {
      return true;
    }

    const referenceDateAsIso = referenceDate.toISOString().split('T')[0];
    const dateToCompareAsIso = dateToCompare.toISOString().split('T')[0];

    return referenceDateAsIso === dateToCompareAsIso;
  };

  const today = new Date();

  // for days after the program has ended, we still want users to be able
  // to make registrations beyond the program duration.
  while (isSameDayOrBefore(currentDate, today)) {
    for (let i = 0; i < 7; i++) {
      daysData[dateFormat(currentDate)] = {
        totalAmount: null,
        goal: null,
        items: [],
      };

      // Go to next day.
      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  return daysData;
}
// End old code.

export function getRegistrationOverview() {
  return (dispatch, getState) => {
    const API_URL = config.get('API_URL');
    const language = getState().getIn(['ui', 'language']);
    const MULTILINGUAL = config.get('MULTILINGUAL', true);
    const PATH = MULTILINGUAL ? `${API_URL}/${language}` : API_URL;

    dispatch(setOperationStatus('registrations', 'overview', UI_OPERATION_STATUS_REQUEST));

    return new Promise((resolve, reject) => {
      fetch(`${PATH}/registrations/overview`, {
        credentials: 'include',
      })
        .then((response) => {
          if (!response.ok) throw new Error();
          return response.json();
        })
        .then((response) => {
          dispatch(registrationOverviewLoaded(response));
          dispatch(setOperationStatus('registrations', 'overview', UI_OPERATION_STATUS_SUCCESS));
          resolve(response);
        })
        .catch((error) => {
          dispatch(setOperationStatus('registrations', 'overview', UI_OPERATION_STATUS_ERROR));
          reject(error);
        });
    });
  };
}

export function getRegistrationStatistics() {
  return (dispatch, getState) => {
    const API_URL = config.get('API_URL');
    const language = getState().getIn(['ui', 'language']);
    const MULTILINGUAL = config.get('MULTILINGUAL', true);
    const PATH = MULTILINGUAL ? `${API_URL}/${language}` : API_URL;

    dispatch(setOperationStatus('registrations', 'statistics', UI_OPERATION_STATUS_REQUEST));

    return new Promise((resolve, reject) => {
      fetch(`${PATH}/registrations/statistics`, {
        credentials: 'include',
      })
        .then((response) => {
          if (!response.ok) throw new Error();
          return response.json();
        })
        .then((response) => {
          dispatch(registrationStatisticsLoaded(response));
          dispatch(setOperationStatus('registrations', 'statistics', UI_OPERATION_STATUS_SUCCESS));
          resolve(response);
        })
        .catch((error) => {
          dispatch(setOperationStatus('registrations', 'statistics', UI_OPERATION_STATUS_ERROR));
          reject(error);
        });
    });
  };
}

export function createRegistration(data) {
  return async (dispatch) => {
    await dispatch(webformSubmit('daily_registration', data))
      .then(() => {
        dispatch(reinitializeRegistrationData());
      });
  };
}

export function updateRegistration(uuid, data) {
  return async (dispatch) => {
    await dispatch(webformSubmit('daily_registration', data, {
      method: 'PATCH',
      uuid,
    })).then(() => {
      dispatch(reinitializeRegistrationData());
    });
  };
}

export function deleteRegistration(date, uuid) {
  return (dispatch, getState) => {
    const API_URL = config.get('API_URL');
    const language = getState().getIn(['ui', 'language']);
    const MULTILINGUAL = config.get('MULTILINGUAL', true);
    const PATH = MULTILINGUAL ? `${API_URL}/${language}` : API_URL;
    const url = `${PATH}/daily_registration/${uuid}?_format=json`;

    setOperationStatus('webform', 'delete', UI_OPERATION_STATUS_REQUEST);

    return new Promise((resolve, reject) => {
      fetch(`${PATH}/rest/session/token`, { credentials: 'include' })
        .then((response) => response.text())
        .then((csrfToken) => fetch(url, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
        })
          .then((response) => {
            if (response.ok) {
              dispatch(setOperationStatus('webform', 'submit', UI_OPERATION_STATUS_SUCCESS));
              dispatch(registrationDeleted(date, uuid));
              dispatch(reinitializeRegistrationData());

              resolve(response);
            } else {
              throw new Error();
            }
          })
          .catch((error) => {
            dispatch(setOperationStatus('webform', 'submit', UI_OPERATION_STATUS_ERROR));
            reject(error);
          })
          .catch((error) => {
            dispatch(setOperationStatus('webform', 'submit', UI_OPERATION_STATUS_ERROR));
            reject(error);
          }));
    });
  };
}

export const INITIALIZE_REGISTRATIONS = 'INITIALIZE_REGISTRATIONS';
export function initializeRegistrations(registrationsStartDate, programStartDate) {
  return {
    type: INITIALIZE_REGISTRATIONS,
    registrationsStartDate,
    programStartDate,
  };
}

export const REGISTRATION_OVERVIEW_LOADED = 'REGISTRATION_OVERVIEW_LOADED';
export function registrationOverviewLoaded(registrations) {
  return {
    type: REGISTRATION_OVERVIEW_LOADED,
    payload: registrations,
  };
}

export const REGISTRATION_STATISTICS_LOADED = 'REGISTRATION_STATISTICS_LOADED';
export function registrationStatisticsLoaded(statistics) {
  return {
    type: REGISTRATION_STATISTICS_LOADED,
    payload: statistics,
  };
}

export const REGISTRATION_DELETED = 'REGISTRATION_DELETED';
export function registrationDeleted(date, uuid) {
  return {
    type: REGISTRATION_DELETED,
    date,
    uuid,
  };
}

export const RESET_REGISTRATIONS = 'RESET_REGISTRATIONS';
export function registrationsReset() {
  return {
    type: RESET_REGISTRATIONS,
  };
}

export function getRegistrationData() {
  return (dispatch) => {
    dispatch(getRegistrationOverview());
    dispatch(getRegistrationStatistics());
  };
}

export function reinitializeRegistrationData() {
  return (dispatch) => {
    dispatch(getRegistrationData());
    dispatch(viewGet('milestones', 'block_1', 0, null, {}));
    dispatch(viewGet('badges', 'block_1', 0, null, {}));
    dispatch(viewGet('exercise_answers', 'default', 0, null, {}));
    dispatch(fetchMessages());
  };
}

export function resetRegistrations() {
  return (dispatch) => {
    dispatch(registrationsReset());
  };
}
